import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { AuthError, useAuthServices } from '../authServices';
import { AuthLayout } from '../AuthLayout';
import { Icon } from '../../common/Icon';

/** QZ-D28: confirms a sign-up code, or resends one, directly against Cognito. */
export const ConfirmSignUpPage = () => {
  const { t } = useTranslation();
  const { cognito } = useAuthServices();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [resent, setResent] = useState(false);

  const errorMessage = (thrown: unknown) =>
    thrown instanceof AuthError ? t(`auth.error.${thrown.code}`) : t('auth.error.Unknown');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await cognito.confirmSignUp(email, code);
      navigate('/signin', { state: { confirmed: true } });
    } catch (thrown) {
      setError(errorMessage(thrown));
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setError(undefined);
    setResent(false);
    setBusy(true);
    try {
      await cognito.resendConfirmationCode(email);
      setResent(true);
    } catch (thrown) {
      setError(errorMessage(thrown));
    } finally {
      setBusy(false);
    }
  };

  const emailFromUrl = searchParams.get('email');

  return (
    <AuthLayout>
      <h1>{t('auth.confirm.heading')}</h1>
      <p className="qz-sub">
        {emailFromUrl === null
          ? t('auth.confirm.enterBoth')
          : t('auth.confirm.sentTo', { email: emailFromUrl })}
      </p>
      {resent && (
        <div className="alert alert-success">
          <Icon name="envelope-circle-check" />
          <div>{t('auth.confirm.resent')}</div>
        </div>
      )}
      {error && (
        <div className="alert alert-danger">
          <Icon name="circle-exclamation" />
          <div>{error}</div>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        {emailFromUrl === null && (
          <div className="mb-3">
            <label htmlFor="confirm-email" className="form-label">
              {t('auth.email')}
            </label>
            <input
              id="confirm-email"
              type="email"
              autoComplete="username"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        )}
        <div className="mb-3">
          <label htmlFor="confirm-code" className="form-label">
            {t('auth.code')}
          </label>
          <input
            id="confirm-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="form-control qz-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary w-100" disabled={busy}>
          {t('auth.confirm.submit')}
        </button>
      </form>
      <div className="text-center mt-3">
        <button
          type="button"
          className="btn btn-link btn-sm"
          disabled={busy}
          onClick={handleResend}
        >
          {t('auth.confirm.resend')}
        </button>
      </div>
    </AuthLayout>
  );
};

export default ConfirmSignUpPage;
